import {
  CartProductCode,
  CurrentPayPlan,
  FlowType,
  MembershipLevel,
  MembershipProductType,
  PriceCartItem,
} from '@ace-clublabs/common-membership-api-types';
import { INewMembershipInfo, PartnerIntegration } from '@lib/types';
import { NewJoinSessionData, PartnerEnrollments } from '../application/models/NewJoinSessionData';
import { ABBREVIATED_MEMBERSHIP_LEVELS } from './benefitsHelper';
import CookieHelper from './cookieHelper';
import { autoPayBillPlans } from './constants';
import { getCartProductName, isAutopayRequired } from './helpers';
import { getPartnerEnrollmentStatuses } from './partnerEnrollmentHelper';
import { hashStringWithSHA256 } from './hash';
import { TransactionStatus } from '@ace-clublabs/payment-types';

export enum UtagType {
  UDO,
  view,
  link,
  dispatch,
}

const partnerProductMap = {
  [PartnerIntegration.PARKING_SPOT]: 'thespotclub',
  [PartnerIntegration.EXPERIAN]: 'pmidessential',
  [PartnerIntegration.HERTZ]: 'loyalty',
};

const partnerCompanyMap = {
  [PartnerIntegration.PARKING_SPOT]: 'theparkingspot',
  [PartnerIntegration.EXPERIAN]: 'experian',
  [PartnerIntegration.HERTZ]: 'hertzgold',
};

export class UtagHelper {
  static loadUtagJs() {
    if ((global as any).utagLoadAttempt) return;
    (global as any).utagLoadAttempt = true;

    const utagJsScript = document.createElement('script');
    utagJsScript.src = '//tms.ace.aaa.com/ace/prod/utag.js';
    utagJsScript.type = 'text/javascript';
    utagJsScript.async = true;
    document.body.appendChild(utagJsScript);
  }

  static view(params: any) {
    (global as any).aceMediaTagValues = { ...params };
    if (typeof (global as any).utag === 'undefined') {
      UtagHelper.loadUtagJs();
    } else {
      (global as any).utag.view((global as any).aceMediaTagValues);
    }
  }

  static link(params: any) {
    (global as any).aceMediaTagValues = { ...params };
    if (typeof (global as any).utag === 'undefined') {
      UtagHelper.loadUtagJs();
    } else {
      (global as any).utag.link((global as any).aceMediaTagValues);
    }
  }

  static buildTags(
    sessionData: NewJoinSessionData,
    membershipInfo: INewMembershipInfo | null | undefined,
    page: string,
    overrides?: object
  ) {
    const { allowedBillPlans, allowedMembershipLevels, defaultBillPlan, requestedMembershipLevel } =
      membershipInfo || {};
    const { offerToken, flowType } = sessionData;
    const MP = CurrentPayPlan.RECURRINGMONTHLY;
    const { membershipLevel, billPlan, firstSelectedMembershipLevel, mobileConsent } = sessionData.formData;
    const isMonthly = billPlan === MP;
    const hasMonthly = allowedBillPlans?.includes(MP);
    const hasOnlyMonthly = hasMonthly && allowedBillPlans?.length === 1;
    const isAutoPayRequired = isAutopayRequired(membershipInfo!, sessionData.formData);
    const offersMoreThanClassic =
      allowedMembershipLevels?.filter((level) => level !== MembershipLevel.CLASSIC)?.length! > 0;
    const membershipLevelAbbreviations = allowedMembershipLevels?.map((level) => ABBREVIATED_MEMBERSHIP_LEVELS[level]);

    const promoFlows = [FlowType.MAJOR_ROCKET, FlowType.T_MOBILE, FlowType.GIFT, FlowType.EMPLOYEE, FlowType.GROUPON];
    const promoType = promoFlows?.includes(flowType!) ? flowType?.toLowerCase()?.replace('_', ' ') : '';

    const billPlanOptions = hasOnlyMonthly ? 'mm' : hasMonthly ? 'all' : 'an';
    const isGiftFlow = flowType === FlowType.GIFT;
    let tags: object = {};

    if (isGiftFlow) {
      tags = {
        campaigncode: sessionData.campaignCode,
        channel: 'membership',
        subchannel1: 'gift',
        state: CookieHelper.getStateFromZipcodeCookie()?.toLowerCase(),
        flow: 'gift',
        page: page,
        sessionid: UtagHelper.getAnalyticsSessionId(),
        ...overrides,
      };
    } else {
      tags = {
        campaigncode: sessionData.campaignCode,
        channel: 'membership',
        subchannel1: `join`,
        state: CookieHelper.getStateFromZipcodeCookie()?.toLowerCase(),
        flow: `join:${billPlanOptions}`,
        flow_selection: `join:${isMonthly ? 'mm' : 'an'}`, // default or what the member selects (dynamic)
        flow_view: `join:${defaultBillPlan === MP ? 'mm' : 'an'}`, // preselected membership pay period (static)
        mem_autopayrequired: `autopay required:${isAutoPayRequired ? 'yes' : 'no'}`,
        mem_level_qualify: `${offersMoreThanClassic ? 'true' : 'false'}`,
        mem_level_view: membershipLevelAbbreviations?.join(':'),
        mem_promo: offerToken ?? '',
        mem_promo_invalid: '', // blank until promo code fn is built
        mem_promo_type: promoType,
        page: `membership:join:${page}`,
        sessionid: UtagHelper.getAnalyticsSessionId(),
        ...overrides,
      };
    }

    if (page !== 'select level') {
      if (isGiftFlow) {
        tags = {
          ...tags,
          mem_level: membershipLevel?.toLowerCase() || requestedMembershipLevel?.toLowerCase(),
        };
      } else {
        tags = {
          ...tags,
          mem_level: membershipLevel?.toLowerCase() || requestedMembershipLevel?.toLowerCase(),
          mem_level_firstselection: firstSelectedMembershipLevel?.toLowerCase(), // This logs the membership level when the user clicks next on the benefits page FIRST TIME ONLY
        };
      }
    }

    if (page == 'select level') {
      tags = {
        ...tags,
        tnt_mem_level: membershipLevel?.toLowerCase() || requestedMembershipLevel?.toLowerCase(),
        tnt_flow_selection: `join:${isMonthly ? 'mm' : 'an'}`,
      };
    }

    const preSelectPages = [
      'autopay',
      'payment',
      'confirmation',
      'confirmation-membership registration success',
      'confirmation-membership registration error',
      'partner confirmation',
      'partner enrollment',
    ];
    if (preSelectPages.includes(page)) {
      tags = {
        ...tags,
        mem_autopay_preselect: `pre select:${autoPayBillPlans?.includes(defaultBillPlan!) ? 'yes' : 'no'}`,
      };
    }

    if (!isGiftFlow && typeof mobileConsent === 'boolean' && page !== 'primary member' && page !== 'select level') {
      tags = { ...tags, mcagr: mobileConsent.toString() };
    }
    return tags;
  }

  static buildProductTags(sessionData: NewJoinSessionData) {
    const priceCartItems = sessionData.priceCart?.items;

    let productTags = {
      product: [],
      quantity: [],
      transaction_amount: [],
      category: [],
    } as {
      product: string[];
      quantity: string[];
      transaction_amount: string[];
      category: string[];
    };

    if (!priceCartItems) {
      return productTags;
    }

    const priceItemMap = {} as { [code: number]: PriceCartItem[] };

    for (let product of priceCartItems) {
      if (priceItemMap[product.code]) {
        priceItemMap[product.code].push(product);
      } else {
        priceItemMap[product.code] = [product];
      }
    }

    const addProductTags = (productName: string, quantity: number, amount: number, discount: boolean = false) => {
      productTags.product.push(`${productName}${discount ? '-DISCOUNT' : ''}`);
      productTags.quantity.push('1');
      productTags.transaction_amount.push(`${discount ? '-' : ''}${amount.toFixed(2)}`);

      switch (sessionData?.flowType) {
        case FlowType.NEW_JOIN:
          productTags.category.push('membership:join');
          break;

        case FlowType.EMPLOYEE:
          productTags.category.push('membership:join employee');
          break;

        case FlowType.GIFT:
          productTags.category.push('membership:gift');
          break;

        default:
          productTags.category.push('membership:join');
      }
    };

    for (let key of Object.keys(priceItemMap)) {
      const code = Number(key);
      const products = priceItemMap[code];
      const firstProduct = priceItemMap[code][0];
      const productName = getCartProductName(code);
      const discountedProducts = priceItemMap[code]?.filter((product) => product.discount! > 0);

      if (code == CartProductCode.AUTOPAY_DISCOUNT) {
        addProductTags('AUTOPAY', products.length, firstProduct?.price, true);
        continue;
      }

      if (code == CartProductCode.RV) {
        addProductTags('RV-ADD', 1, firstProduct?.price);
        continue;
      }
      for (let i = 0; i < products.length; i++) {
        addProductTags(productName, 1, firstProduct?.price);
      }

      if (discountedProducts.length) {
        addProductTags(productName, discountedProducts.length, discountedProducts[0].discount!, true);
      }
    }

    return productTags;
  }

  static dispatchTntSpaEvent(tags: object) {
    document.dispatchEvent(
      new CustomEvent('tnt_spa', {
        detail: tags,
      })
    );
  }

  static getAnalyticsSessionId() {
    const utagCookie = CookieHelper.getCookie('utag_main');
    if (utagCookie) {
      const parse = utagCookie.split('$ses_id:')[1];
      const sessionId = parse.split('%3')[0];
      return sessionId ?? '';
    }

    return '';
  }

  static buildOnClickRoadsideTags(sessionData: NewJoinSessionData, membershipInfo: INewMembershipInfo) {
    return {
      ...UtagHelper.buildRegistrationTags(sessionData, membershipInfo),
      cta: 'membership:join:partner confirmation-roadside assistance chatbot click',
      hd_olm_1: hashStringWithSHA256(sessionData.confirmationData?.email || ''),
      hd_olm_2: hashStringWithSHA256(sessionData.formData?.mobileNumber || ''),
    };
  }

  static buildRegistrationTags(sessionData: NewJoinSessionData, membershipInfo: INewMembershipInfo) {
    const {
      confirmationData: {
        options,
        members,
        confirmationId,
        autoPayStatus,
        membershipLevel: purchasedMembershipLevel,
      } = {},
    } = sessionData;
    const custID = confirmationId?.slice(0, 15);
    const isEnrolledInAutoPay = autoPayStatus === TransactionStatus.Succeeded;
    const autoPayTag = isEnrolledInAutoPay ? 'olm enroll' : '';

    const ableToBuyErs = membershipInfo?.membershipProducts?.ERS?.includes(purchasedMembershipLevel as MembershipLevel);
    const numOfAssociates = members && Array.isArray(members) ? members.length : 0;
    const boughtErs = options?.includes(MembershipProductType.ERS);

    const mem_associate = numOfAssociates.toString();
    const mem_benefitlevel = 'MBRSHP_BASIC'; //TODO is this dynamic
    const mem_ers = boughtErs ? 'true' : ableToBuyErs ? 'false' : 'na';
    const mem_primary = '1';

    return {
      custID,
      autopay: autoPayTag,
      mem_associate,
      mem_benefitlevel,
      mem_ers,
      mem_primary,
    };
  }

  static buildStatelessBaseTags(confirmationTags: Record<string, string>) {
    //variables will duplicate confirmation count if fired on link
    const removeDupeConfirmationTags = [
      'category',
      'confirmation_id',
      'ins_quoteid',
      'product',
      'quantity',
      'total',
      'transaction_amount',
      'transaction_id',
      'transaction_type',
    ];
    return Object.keys(confirmationTags)
      .filter((tag) => !removeDupeConfirmationTags.includes(tag))
      .reduce(
        (acc, key) => {
          acc[key] = confirmationTags[key];
          return acc;
        },
        {} as Record<string, string>
      );
  }

  static buildBasePartnerIntegrationTags(sessionData: NewJoinSessionData, membershipInfo: INewMembershipInfo) {
    const {
      formData: { mobileNumber },
      confirmationData: { email } = {},
    } = sessionData;

    return {
      ...UtagHelper.buildRegistrationTags(sessionData, membershipInfo),
      hd_olm_1: hashStringWithSHA256(email || ''),
      hd_olm_2: hashStringWithSHA256(mobileNumber || ''),
    };
  }

  static buildPartnerIntegrationDefaultSelectionTags(
    sessionData: NewJoinSessionData,
    membershipInfo: INewMembershipInfo
  ) {
    const partnerIntegrations = sessionData?.partnerOffers?.availableOffers;

    if (partnerIntegrations !== undefined && Array.isArray(partnerIntegrations) && partnerIntegrations.length === 0) {
      return {};
    }

    const partnerEnrollmentsTags = partnerIntegrations?.map((partner) => {
      const partnerName = partner.name;
      const partnerCompany = partnerCompanyMap[partnerName];
      const partnerProduct = partnerProductMap[partnerName];
      const isSelectedByDefault = partner.isSelectedByDefault;

      return `${partnerCompany}:${partnerProduct}-${isSelectedByDefault ? 't' : 'f'}`;
    });

    return {
      ...UtagHelper.buildBasePartnerIntegrationTags(sessionData, membershipInfo),
      partner_enroll: partnerEnrollmentsTags || [],
    };
  }

  static buildPartnerEnrollmentFinalSelectionTags(sessionData: NewJoinSessionData, membershipInfo: INewMembershipInfo) {
    const partnerIntegrations = sessionData?.partnerOffers?.availableOffers;
    if (partnerIntegrations !== undefined && Array.isArray(partnerIntegrations) && partnerIntegrations.length === 0) {
      return {};
    }

    const partnerEnrollmentSelections = (sessionData.formData?.partnerEnrollments || {}) as PartnerEnrollments;

    const partnerEnrollmentsSelectionTags = partnerIntegrations?.map((partner) => {
      const partnerCompany = partnerCompanyMap[partner.name];
      const partnerProduct = partnerProductMap[partner.name];
      const partnerSelected = partnerEnrollmentSelections[partner.name];

      return `${partnerCompany}:${partnerProduct}-${partnerSelected ? 't' : 'f'}`;
    });

    return {
      ...UtagHelper.buildBasePartnerIntegrationTags(sessionData, membershipInfo),
      partner_enroll: partnerEnrollmentsSelectionTags,
    };
  }

  static buildPartnerEnrollmentErrorStatusTags(sessionData: NewJoinSessionData) {
    const partnerEnrollmentFailureTagNameMap = {
      [PartnerIntegration.PARKING_SPOT]: 'parking spot',
      [PartnerIntegration.EXPERIAN]: 'experian',
      [PartnerIntegration.HERTZ]: 'hertz',
    };

    const { failedPartnerLinkages, failedPartnerRegistrations } = getPartnerEnrollmentStatuses(sessionData);

    const errorMessages: string[] = [];

    const partnerEnrollmentErrorTag = `membership:join:partner confirmation-failed`;

    if (failedPartnerRegistrations.length > 0) {
      const failedRegistrations = failedPartnerRegistrations
        .map((partner) => partnerEnrollmentFailureTagNameMap[partner.name])
        .join(',');
      const failedRegistrationTag = `${partnerEnrollmentErrorTag} registration:${failedRegistrations}`;
      errorMessages.push(failedRegistrationTag);
    }

    if (failedPartnerLinkages.length > 0) {
      const failedLinkages = failedPartnerLinkages
        .map((partner) => partnerEnrollmentFailureTagNameMap[partner.name])
        .join(',');
      const failedLinkageTag = `${partnerEnrollmentErrorTag} membership link:${failedLinkages}`;
      errorMessages.push(failedLinkageTag);
    }

    return errorMessages.length > 0 ? { error: errorMessages } : {};
  }
}
